
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSecureAdminManagement } from '../hooks/useSecureAdminManagement';
import { useFeedbackNotifications } from '../hooks/useFeedbackNotifications';
import { useModerationNotifications } from '../hooks/useModerationNotifications';
import { getUserRole } from '../services/moderationService';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Trash2, UserPlus, Shield, ShieldCheck, History, AlertTriangle, MessageSquare, Bell, AlertOctagon } from 'lucide-react';
import { Skeleton } from './ui/skeleton';
import { format } from 'date-fns';

const SecureAdminManagementPanel = () => {
  const { t } = useTranslation();
  const { 
    adminUsers, 
    auditLog,
    isLoading, 
    isAdmin, 
    addAdmin, 
    updateAdmin, 
    removeAdmin,
    isAdding,
    isUpdating,
    isRemoving 
  } = useSecureAdminManagement();
  
  const [newAdminIdentifier, setNewAdminIdentifier] = useState('');
  const [newAdminRole, setNewAdminRole] = useState<'admin' | 'moderator'>('moderator');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Get user role for feedback notifications
  useEffect(() => {
    const checkUserRole = async () => {
      const role = await getUserRole();
      setUserRole(role);
    };
    checkUserRole();
  }, []);

  const { pendingCount, latestFeedback } = useFeedbackNotifications(userRole);
  const { pendingCount: pendingReportsCount, latestReport } = useModerationNotifications(userRole);

  if (!isAdmin) {
    return (
      <div className="container mx-auto max-w-4xl p-4">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Shield className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('adminPanel.accessDenied')}</h3>
              <p className="text-muted-foreground">
                {t('adminPanel.onlyAdminsCanAccess')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleAddAdmin = () => {
    const identifier = newAdminIdentifier.trim();
    if (!identifier) {
      return;
    }

    addAdmin({ identifier, role: newAdminRole });
    setNewAdminIdentifier('');
    setNewAdminRole('moderator');
    setShowAddDialog(false);
  };

  const handleUpdateRole = (adminId: string, newRole: 'admin' | 'moderator') => {
    updateAdmin({ adminId, role: newRole });
  };

  const handleRemoveAdmin = (adminId: string, userName?: string) => {
    if (confirm(t('adminPanel.secureAdmin.confirmRemove', { name: userName || t('adminPanel.secureAdmin.thisUser') }))) {
      removeAdmin(adminId);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" />
          {t('adminPanel.adminRoles.admin')}
        </Badge>;
      case 'moderator':
        return <Badge variant="secondary" className="flex items-center gap-1">
          <Shield className="h-3 w-3" />
          {t('adminPanel.adminRoles.moderator')}
        </Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'ADMIN_ROLE_GRANTED':
        return <Badge variant="default">{t('adminPanel.secureAdmin.roleGranted')}</Badge>;
      case 'ADMIN_ROLE_UPDATED':
        return <Badge variant="secondary">{t('adminPanel.secureAdmin.roleUpdated')}</Badge>;
      case 'ADMIN_ROLE_REVOKED':
        return <Badge variant="destructive">{t('adminPanel.secureAdmin.roleRevoked')}</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl p-4 space-y-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl p-4">
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">{t('adminPanel.secureAdmin.adminUsers')}</TabsTrigger>
          <TabsTrigger value="feedback">{t('navigation.feedbackManagement')}</TabsTrigger>
          <TabsTrigger value="moderation">{t('adminPanel.moderation.title')}</TabsTrigger>
          <TabsTrigger value="audit">{t('adminPanel.secureAdmin.auditLog')}</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  {t('adminPanel.adminManagement.title')}
                  <Badge variant="outline">{t('adminPanel.adminManagement.usersCount', { count: adminUsers.length })}</Badge>
                </CardTitle>
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <UserPlus className="h-4 w-4 mr-2" />
                      {t('adminPanel.secureAdmin.addAdminModerator')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('adminPanel.adminManagement.addNewTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">{t('adminPanel.secureAdmin.emailOrUserId')}</label>
                        <Input
                          value={newAdminIdentifier}
                          onChange={(e) => setNewAdminIdentifier(e.target.value)}
                          placeholder={t('adminPanel.secureAdmin.emailOrUuidPlaceholder')}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('adminPanel.secureAdmin.enterEmailOrUuid')}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t('adminPanel.adminManagement.roleLabel')}</label>
                        <Select value={newAdminRole} onValueChange={(value: 'admin' | 'moderator') => setNewAdminRole(value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="moderator">{t('adminPanel.adminManagement.roleModerator')}</SelectItem>
                            <SelectItem value="admin">{t('adminPanel.adminManagement.roleAdmin')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 pt-4">
                        <Button
                          onClick={handleAddAdmin}
                          disabled={isAdding}
                          className="flex-1"
                        >
                          {isAdding ? t('adminPanel.adminManagement.adding') : t('adminPanel.adminManagement.addUser')}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowAddDialog(false)}
                          className="flex-1"
                        >
                          {t('common.cancel')}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {adminUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('adminPanel.adminManagement.noAdministrators')}</h3>
                  <p className="text-muted-foreground">
                    {t('adminPanel.adminManagement.noAdministratorsDesc')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {adminUsers.map((admin) => (
                    <Card key={admin.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">
                                {admin.user_name || t('adminPanel.adminManagement.unknownUser')}
                              </h4>
                              {getRoleBadge(admin.role)}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {t('adminPanel.adminManagement.idLabel')}: {admin.user_id}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {t('adminPanel.adminManagement.addedLabel')}: {format(new Date(admin.created_at), 'MMM dd, yyyy HH:mm')}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Select
                              value={admin.role}
                              onValueChange={(value: 'admin' | 'moderator') => 
                                handleUpdateRole(admin.id, value)
                              }
                              disabled={isUpdating}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="moderator">{t('adminPanel.adminManagement.roleModerator')}</SelectItem>
                                <SelectItem value="admin">{t('adminPanel.adminManagement.roleAdmin')}</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveAdmin(admin.id, admin.user_name || undefined)}
                              disabled={isRemoving}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="feedback">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  {t('navigation.feedbackManagement')}
                  <Badge variant={pendingCount > 0 ? 'destructive' : 'secondary'}>
                    {t('adminPanel.secureAdmin.pendingCount', { count: pendingCount })}
                  </Badge>
                </CardTitle>
                <Button asChild>
                  <Link to="/feedback-management">
                    {t('adminPanel.secureAdmin.viewAllFeedback')}
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {/* Pending Feedback Summary */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Bell className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('adminPanel.secureAdmin.pendingFeedback')}</h4>
                      <p className="text-sm text-muted-foreground">
                        {t('adminPanel.secureAdmin.feedbackWaiting', { count: pendingCount })}
                      </p>
                    </div>
                  </div>
                  <Badge variant={pendingCount > 0 ? 'destructive' : 'secondary'} className="text-lg px-3 py-1">
                    {pendingCount}
                  </Badge>
                </div>

                {/* Latest Feedback Item */}
                {latestFeedback && (
                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="pt-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive">{t('adminPanel.secureAdmin.latest')}</Badge>
                          <Badge variant="outline">{latestFeedback.feedback_type}</Badge>
                        </div>
                        <h4 className="font-medium">{latestFeedback.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {t('adminPanel.secureAdmin.from')}: {latestFeedback.anonymous_name || t('adminPanel.secureAdmin.registeredUser')} • {format(new Date(latestFeedback.created_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {pendingCount === 0 && (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t('adminPanel.secureAdmin.allCaughtUp')}</h3>
                    <p className="text-muted-foreground">
                      {t('adminPanel.secureAdmin.noPendingFeedback')}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="moderation">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <AlertOctagon className="h-5 w-5" />
                  {t('adminPanel.secureAdmin.reportModeration')}
                  <Badge variant={pendingReportsCount > 0 ? 'destructive' : 'secondary'}>
                    {t('adminPanel.moderation.pendingCount', { count: pendingReportsCount })}
                  </Badge>
                </CardTitle>
                <Button asChild>
                  <Link to="/moderation">
                    {t('adminPanel.secureAdmin.viewModerationPanel')}
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {/* Pending Reports Summary */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-destructive/10 rounded-full flex items-center justify-center">
                      <AlertOctagon className="h-5 w-5 text-destructive" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('adminPanel.secureAdmin.pendingReports')}</h4>
                      <p className="text-sm text-muted-foreground">
                        {t('adminPanel.secureAdmin.reportsWaiting', { count: pendingReportsCount })}
                      </p>
                    </div>
                  </div>
                  <Badge variant={pendingReportsCount > 0 ? 'destructive' : 'secondary'} className="text-lg px-3 py-1">
                    {pendingReportsCount}
                  </Badge>
                </div>

                {/* Latest Report Item */}
                {latestReport && (
                  <Card className="border-l-4 border-l-red-500">
                    <CardContent className="pt-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive">{t('adminPanel.secureAdmin.latest')}</Badge>
                          <Badge variant="outline">{latestReport.reason}</Badge>
                        </div>
                        <h4 className="font-medium">{t('adminPanel.moderation.reportNumber', { id: latestReport.id.slice(0, 8) })}</h4>
                        <p className="text-sm text-muted-foreground">
                          {t('adminPanel.moderation.reportedBy')}: {latestReport.reporter_name || t('adminPanel.moderation.anonymous')} • {format(new Date(latestReport.created_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                        {latestReport.pet_name && (
                          <p className="text-sm text-muted-foreground">
                            {t('adminPanel.moderation.pet')}: {latestReport.pet_name}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {pendingReportsCount === 0 && (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t('adminPanel.secureAdmin.allClear')}</h3>
                    <p className="text-muted-foreground">
                      {t('adminPanel.secureAdmin.noPendingReports')}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                {t('adminPanel.secureAdmin.auditLog')}
                <Badge variant="outline">{t('adminPanel.secureAdmin.entriesCount', { count: auditLog.length })}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {auditLog.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('adminPanel.secureAdmin.noAuditEntries')}</h3>
                  <p className="text-muted-foreground">
                    {t('adminPanel.secureAdmin.auditActionsAppear')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLog.map((entry) => (
                    <Card key={entry.id} className="border-l-4 border-l-orange-500">
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              {getActionBadge(entry.action)}
                              <span className="text-sm text-muted-foreground">
                                {format(new Date(entry.created_at), 'MMM dd, yyyy HH:mm:ss')}
                              </span>
                            </div>
                            <p className="text-sm">
                              Admin: <code className="bg-muted px-1 rounded">{entry.admin_user_id}</code>
                            </p>
                            {entry.target_user_id && (
                              <p className="text-sm">
                                Target: <code className="bg-muted px-1 rounded">{entry.target_user_id}</code>
                              </p>
                            )}
                            {entry.details && (
                              <div className="text-xs text-muted-foreground">
                                <pre className="bg-muted p-2 rounded mt-2 overflow-x-auto">
                                  {JSON.stringify(entry.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecureAdminManagementPanel;
